import { Box, Tooltip, Typography } from '@mui/material';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import AudioContentPanel from './AudioContentPanel/AudioContentPanel';
import usePractitionerControlsContext from '../../../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import ImageContentPanel from './ImageContentPanel/ImageContentPanel';
import VideoContentPanel from './VideoContentPanel/VideoContentPanel';
import { OnlinePanelStyle } from './style';
import { useTranslation } from 'react-i18next';
import ButtonUI from '../../../../UI/Button/Button';
import BackendService from '../../../../../services/BackendService';
import useAppContext from '../../../../../hooks/useAppContext/useAppContext';
import useMediaContext from '../../../../../hooks/useMediaContextHook/useMediaContext';
import { MediaTypes } from '../../../../../commands/practitioner/enums/MediaTypes';
import MediaInput from './Mediainput/MediaInput';
import CombineToggle from '../../../../UI/CombineToggle/CombineToggle';
import Flat from '../../../../../assets/icons/Flat.svg?react';
import Sound from '../../../../../assets/icons/AudioType.svg?react';

interface practitionerMediaTypeInterface {
  type: 'text';
  content: 'Video' | 'Audio';
  startIcon?: ReactNode;
  isActive: boolean;
}

interface TabPanelProps {
  className?: string;
  index: number;
  value: number;
}

export function OnlinePanel(props: TabPanelProps) {
  const { value, index } = props;
  const {
    practitionerMediaType,
    isMediaLoaded,
    projectionType,
    practitionerMediaURI,
    setOpenMediaModal,
    setHasBeenSaved,
    setStartResetTimeout,
    setPractitionerMediaURI,
  } = usePractitionerControlsContext();
  useEffect(() => {
    setPractitionerMediaURI('');
  }, []);

  const { setDiveIndex } = useMediaContext();
  const { t } = useTranslation();
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const { onlineContainer } = OnlinePanelStyle;
  const isSaveDisabled = Boolean(!practitionerMediaURI && !isMediaLoaded);
  const [isAudioChecked, setIsAudioChecked] = useState<boolean>(false);
  const showVideoAudioSwitcher =
    (practitionerMediaType === MediaTypes.VIDEO && !!practitionerMediaURI && isMediaLoaded) || isAudioChecked;
  const practitionerMediaTypeOptions: practitionerMediaTypeInterface[] = useMemo(
    () => [
      {
        type: 'text',
        content: 'Video',
        startIcon: <Flat />,
        isActive: isAudioChecked ? false : true,
      },
      {
        type: 'text',
        content: 'Audio',
        startIcon: <Sound />,
        isActive: isAudioChecked ? true : false,
      },
    ],
    [t, isAudioChecked]
  );

  const renderCorrectTypeTab = useMemo(() => {
    switch (practitionerMediaType) {
      case MediaTypes.VIDEO:
        return <VideoContentPanel index={0} value={practitionerMediaType} />;
        break;
      case MediaTypes.IMAGE:
        return <ImageContentPanel index={1} value={practitionerMediaType} />;
        break;
      case MediaTypes.AUDIO:
        return hasDevice ? null : <AudioContentPanel index={2} value={practitionerMediaType} />;
        break;
      default:
        return <VideoContentPanel index={0} value={practitionerMediaType} />;
    }
  }, [practitionerMediaType]);

  const closeModal = useCallback(() => {
    setOpenMediaModal(false);
    setDiveIndex([]);
  }, [setDiveIndex, setOpenMediaModal]);

  const saveClicked = async () => {
    if (isMediaLoaded) {
      // Send loading to the practitioner to update the state
      await BackendService.sendMediaStatusReport(sessionId, treatmentId, practitionerMediaURI, 0, 0);
      await BackendService.sendLoadMedia(
        sessionId,
        treatmentId,
        practitionerMediaURI,
        practitionerMediaType,
        projectionType,
        'Off'
      );
    }
    setHasBeenSaved(true);
    setOpenMediaModal(false);
    setStartResetTimeout(true);
  };

  const onAudioTypeSwitch = (): void => {
    if (isAudioChecked) {
      setIsAudioChecked(false);
    } else if (!isAudioChecked) {
      setIsAudioChecked(true);
    }
  };

  return (
    <>
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        sx={onlineContainer}
      >
        {value === index && (
          <>
            <Box className={'top-tabs_container'}>
              <Typography className="title">{t('online-panel-title')}</Typography>
            </Box>
            <Box className={'content-container'}>
              <MediaInput isAudioChecked={isAudioChecked} index={index} />
              {showVideoAudioSwitcher && (
                <CombineToggle
                  className={'audio-type-switch'}
                  selectValue={!isAudioChecked}
                  options={practitionerMediaTypeOptions}
                  onChangeHandler={onAudioTypeSwitch}
                  title={'media-modal-content-type'}
                />
              )}
              {renderCorrectTypeTab}
            </Box>
          </>
        )}
      </Box>
      <Box className={'apply-buttons'}>
        <ButtonUI
          className={'cancel-btn'}
          text={t('media-modal-cancel')}
          onClickHandler={closeModal}
          variant={'outlined'}
        />
        <ButtonUI
          className={'save-button'}
          text={t('media-modal-save')}
          onClickHandler={saveClicked}
          disabled={isSaveDisabled}
        />
      </Box>
    </>
  );
}
